<template>
  <div>
    <app-template />
    <div
      class="uk-container uk-container-large app-activity with-drawer"
      :class="{ 'nav-collapsed': navCollapsed }"
    >
      <h2>App Activity</h2>
      <p class="subtitle">
        Who is using the app and who has gone quiet — so you can nudge the
        people who need it.
      </p>

      <div v-if="!isAdmin" class="notice">
        This report is available to club admins only.
      </div>

      <template v-else>
        <SectionNav
          title="App Activity"
          :items="sectionNavItems"
          :active="selectedSection"
          v-model:collapsed="navCollapsed"
          @select="onSelectSection"
        />

        <div class="tabs-wrapper">
          <div v-if="loading" class="notice">Loading activity…</div>
          <div v-else-if="error" class="notice error">{{ error }}</div>

          <template v-else>
            <!-- Summary tiles -->
            <div class="tiles">
              <div class="tile">
                <div class="tile-num">{{ summary.adoption_pct }}%</div>
                <div class="tile-label">Adoption</div>
                <div class="tile-sub">{{ summary.total - summary.never }}/{{ summary.total }} activated</div>
              </div>
              <div class="tile">
                <div class="tile-num">{{ summary.active_7d }}</div>
                <div class="tile-label">Active this week</div>
              </div>
              <div class="tile">
                <div class="tile-num">{{ summary.active_30d }}</div>
                <div class="tile-label">Active this month</div>
              </div>
              <div class="tile warn">
                <div class="tile-num">{{ summary.dormant }}</div>
                <div class="tile-label">Dormant (30+ days)</div>
              </div>
              <div class="tile danger">
                <div class="tile-num">{{ summary.never }}</div>
                <div class="tile-label">Never opened</div>
              </div>
              <div class="tile warn tile-click" @click="pushFilter = 'off'">
                <div class="tile-num">{{ summary.push_off }}</div>
                <div class="tile-label">No push notifications</div>
                <div class="tile-sub">declined or web only</div>
              </div>
            </div>

            <!-- Actionable callout -->
            <div
              v-if="summary.never + summary.dormant > 0"
              class="nudge"
              @click="statusFilter = 'inactive'"
            >
              <strong>{{ summary.never + summary.dormant }}</strong>
              {{ sectionNoun }} haven't opened the app recently — click to see who
              needs a nudge.
            </div>

            <!-- Filters -->
            <div class="filters">
              <select v-if="teamOptions.length" v-model="teamFilter">
                <option value="">All teams</option>
                <option v-for="t in teamOptions" :key="t" :value="t">{{ t }}</option>
              </select>
              <select v-model="statusFilter">
                <option value="">All statuses</option>
                <option value="active">Active (7 days)</option>
                <option value="recent">Recent (8–30 days)</option>
                <option value="dormant">Dormant (30+ days)</option>
                <option value="opened">Opened (date unknown)</option>
                <option value="never">Never opened</option>
                <option value="inactive">Needs a nudge (dormant + never)</option>
              </select>
              <select v-model="pushFilter">
                <option value="">All push</option>
                <option value="off">Push off (declined / web only)</option>
                <option value="on">Push on</option>
              </select>
              <select v-model="platformFilter">
                <option value="">All platforms</option>
                <option value="ios">iPhone app</option>
                <option value="android">Android app</option>
                <option value="web">Web</option>
                <option value="unknown">Not recorded</option>
              </select>
              <select v-if="versionOptions.length" v-model="versionFilter">
                <option value="">All app versions</option>
                <option v-for="v in versionOptions" :key="v" :value="v">{{ v }}</option>
                <option value="unknown">Version not recorded</option>
              </select>
              <span class="count">{{ filteredMembers.length }} shown</span>
            </div>

            <!-- Roster table -->
            <table class="activity-table">
              <thead>
                <tr>
                  <th>{{ sectionColLabel }}</th>
                  <th>Team</th>
                  <th>Last seen</th>
                  <th>Status</th>
                  <th>Push</th>
                  <th>Last used on</th>
                  <th>App version</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="m in filteredMembers" :key="m.id">
                  <td>{{ m.name }}</td>
                  <td>{{ m.teamName || "—" }}</td>
                  <td>{{ lastSeenLabel(m) }}</td>
                  <td>
                    <span class="chip" :class="'chip-' + m.status">
                      {{ statusLabel(m.status) }}
                    </span>
                  </td>
                  <td>
                    <span class="push" :class="'push-' + m.push">{{ pushLabel(m.push) }}</span>
                  </td>
                  <td>{{ platformLabel(m.platform) }}</td>
                  <td>
                    <span
                      v-if="m.app_version"
                      class="version"
                      :class="{ 'version-old': isOutdated(m) }"
                      :title="isOutdated(m) ? 'Newer build available' : ''"
                    >{{ m.app_version }}</span>
                    <span v-else class="muted">—</span>
                  </td>
                </tr>
                <tr v-if="filteredMembers.length === 0">
                  <td colspan="7" class="empty">No {{ sectionNoun }} match this filter.</td>
                </tr>
              </tbody>
            </table>

            <p class="footnote">
              "Opened" means the account has signed in at least once but we don't
              yet have a recent timestamp (web users, or push declined) — it
              resolves to a date once they next open the app. "Push: Off" means
              they won't receive notifications (declined push, or web only).
              Accounts are grouped per login, so family members share a
              last-seen time. "Last used on" and "App version" come from the
              most recent open; phone builds before 1.0.8 don't report a
              version, so "—" on an iPhone/Android row usually means an older
              app that needs updating from the store.
            </p>
          </template>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="js">
import AppTemplate from "../layouts/App";
import SectionNav from "@/components/SectionNav/index.vue";
import { mapGetters } from "vuex";
import moment from "moment";
import { AppActivity } from "@/services/app-activity-service.js";

const SECTION_ROLE = {
  players: "User",
  coaches: "Coach",
  admins: "Admin",
  scouts: "Scout",
  archived: "Archived",
};
const SECTION_NOUN = {
  players: "players",
  coaches: "coaches",
  admins: "admins",
  scouts: "scouts",
  archived: "archived accounts",
};
const SECTION_COL = {
  players: "Player",
  coaches: "Coach",
  admins: "Admin",
  scouts: "Scout",
  archived: "Member",
};

// Sort "1.0.8+19" / "1.1.832" newest first: numeric segments, then build number.
function compareVersionDesc(a, b) {
  const parse = (v) => String(v).split(/[.+]/).map((n) => parseInt(n, 10) || 0);
  const pa = parse(a), pb = parse(b);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pb[i] || 0) - (pa[i] || 0);
    if (d !== 0) return d;
  }
  return 0;
}

export default {
  name: "AppActivityReport",
  components: { AppTemplate, SectionNav },
  data() {
    return {
      userData: { id: null, clubId: "", userType: "" },
      loading: true,
      error: "",
      allMembers: [],
      selectedSection: "players",
      navCollapsed: false,
      teamFilter: "",
      statusFilter: "",
      pushFilter: "",
      platformFilter: "",
      versionFilter: "",
    };
  },
  computed: {
    ...mapGetters(["getProfile"]),
    isAdmin() {
      return this.userData.userType === "Admin";
    },
    sectionNavItems() {
      return [
        { key: "players", label: "Players", icon: "groups" },
        { key: "coaches", label: "Coaches", icon: "sports" },
        { key: "admins", label: "Admins", icon: "admin_panel_settings" },
        { key: "scouts", label: "Scouts", icon: "person_search" },
        { key: "archived", label: "Archived", icon: "archive" },
      ];
    },
    sectionNoun() {
      return SECTION_NOUN[this.selectedSection] || "people";
    },
    sectionColLabel() {
      return SECTION_COL[this.selectedSection] || "Member";
    },
    sectionMembers() {
      const role = SECTION_ROLE[this.selectedSection];
      return this.allMembers.filter((m) => m.userType === role);
    },
    summary() {
      const s = {
        total: 0, active_7d: 0, active_30d: 0, dormant: 0,
        opened: 0, never: 0, push_off: 0, adoption_pct: 0,
      };
      for (const m of this.sectionMembers) {
        s.total++;
        if (m.status === "active") { s.active_7d++; s.active_30d++; }
        else if (m.status === "recent") { s.active_30d++; }
        else if (m.status === "dormant") { s.dormant++; }
        else if (m.status === "opened") { s.opened++; }
        else { s.never++; }
        if (m.push === "off") s.push_off++;
      }
      // Adoption = anyone who has ever opened it (everything except "never").
      s.adoption_pct = s.total > 0 ? Math.round(((s.total - s.never) / s.total) * 100) : 0;
      return s;
    },
    teamOptions() {
      const names = this.sectionMembers.map((m) => m.teamName).filter((n) => !!n);
      return [...new Set(names)].sort();
    },
    // Distinct reported versions in this section, newest first.
    versionOptions() {
      const vs = this.sectionMembers.map((m) => m.app_version).filter((v) => !!v);
      return [...new Set(vs)].sort(compareVersionDesc);
    },
    // Highest build seen per platform — anything below it is "outdated".
    latestVersionByPlatform() {
      const latest = {};
      for (const m of this.allMembers) {
        if (!m.platform || !m.app_version) continue;
        if (!latest[m.platform] || compareVersionDesc(m.app_version, latest[m.platform]) < 0) {
          latest[m.platform] = m.app_version;
        }
      }
      return latest;
    },
    filteredMembers() {
      return this.sectionMembers.filter((m) => {
        if (this.teamFilter && m.teamName !== this.teamFilter) return false;
        if (this.pushFilter && m.push !== this.pushFilter) return false;
        if (this.platformFilter && (m.platform || "unknown") !== this.platformFilter) return false;
        if (this.versionFilter && (m.app_version || "unknown") !== this.versionFilter) return false;
        if (!this.statusFilter) return true;
        if (this.statusFilter === "inactive") {
          return m.status === "dormant" || m.status === "never";
        }
        return m.status === this.statusFilter;
      });
    },
  },
  created() {
    // Copy (never bind directly to store state — Vue 3 reactivity guard).
    this.userData = { ...(this.getProfile || {}) };
    if (this.isAdmin) {
      this.load();
    } else {
      this.loading = false;
    }
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = "";
      try {
        const res = await AppActivity.getAppActivity(
          this.userData.clubId,
          this.userData.id,
        );
        const body = res && res.data;
        if (body && body.success) {
          this.allMembers = body.members || [];
        } else if (res && res.featureDisabled) {
          this.error = "App Activity isn't enabled for this club.";
        } else {
          this.error = (body && body.error) || "Couldn't load app activity.";
        }
      } catch (e) {
        this.error = "Couldn't load app activity.";
      } finally {
        this.loading = false;
      }
    },
    onSelectSection(key) {
      this.selectedSection = key;
      // Reset filters when switching sections so stale filters don't hide rows.
      this.teamFilter = "";
      this.statusFilter = "";
      this.pushFilter = "";
      this.platformFilter = "";
      this.versionFilter = "";
    },
    platformLabel(platform) {
      return { ios: "iPhone app", android: "Android app", web: "Web", flutter: "App" }[platform] || "—";
    },
    isOutdated(m) {
      const latest = this.latestVersionByPlatform[m.platform];
      return !!(latest && m.app_version && compareVersionDesc(m.app_version, latest) > 0);
    },
    lastSeenLabel(m) {
      if (m.last_seen) return moment(m.last_seen).fromNow();
      if (m.status === "opened") return "Opened (date unknown)";
      return "Never";
    },
    statusLabel(status) {
      return {
        active: "Active",
        recent: "Recent",
        dormant: "Dormant",
        opened: "Opened",
        never: "Never opened",
      }[status] || status;
    },
    pushLabel(push) {
      return { on: "On", off: "Off", unknown: "—" }[push] || "—";
    },
  },
};
</script>

<style scoped lang="scss">
/* Section side-drawer layout (SectionNav). Fixed left rail; page content
   padded left to clear it. Mirrors the Profile/Trialist page pattern. */
.app-activity.with-drawer {
  --section-nav-w: 260px;
  padding-left: calc(var(--section-nav-w) + 28px);
  transition: padding-left 0.2s ease;
}
.app-activity.with-drawer.nav-collapsed {
  --section-nav-w: 72px;
}
@media (max-width: 768px) {
  .app-activity.with-drawer,
  .app-activity.with-drawer.nav-collapsed {
    padding-left: 0;
  }
}

.app-activity {
  padding-bottom: 3rem;

  h2 { margin-bottom: 0.25rem; }
  .subtitle { color: #666; margin-bottom: 1.5rem; }

  .notice {
    padding: 1rem 1.25rem;
    background: #f8f9fa;
    border-radius: 8px;
    color: #555;
    &.error { background: #fdecec; color: #b00020; }
  }

  .tiles {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 1rem;
    margin-bottom: 1.25rem;
  }
  .tile {
    background: #fff;
    border: 1px solid #eee;
    border-radius: 10px;
    padding: 1rem 1.25rem;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
    .tile-num { font-size: 1.9rem; font-weight: 700; color: var(--brand-color, #16213e); }
    .tile-label { font-size: 0.85rem; color: #555; margin-top: 0.15rem; }
    .tile-sub { font-size: 0.75rem; color: #999; margin-top: 0.2rem; }
    &.warn .tile-num { color: #c77700; }
    &.danger .tile-num { color: #b00020; }
  }

  .nudge {
    background: #fff8e6;
    border: 1px solid #f0d98a;
    border-radius: 8px;
    padding: 0.85rem 1.1rem;
    margin-bottom: 1.25rem;
    color: #7a5c00;
    cursor: pointer;
    &:hover { background: #fdf3d6; }
  }

  .filters {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
    flex-wrap: wrap;
    select {
      padding: 0.45rem 0.6rem;
      border: 1px solid #ddd;
      border-radius: 6px;
      background: #fff;
    }
    .count { color: #888; font-size: 0.85rem; }
  }

  .activity-table {
    width: 100%;
    border-collapse: collapse;
    background: #fff;
    border: 1px solid #eee;
    border-radius: 10px;
    overflow: hidden;
    th, td {
      text-align: left;
      padding: 0.7rem 1rem;
      border-bottom: 1px solid #f0f0f0;
      font-size: 0.9rem;
    }
    th { background: #fafafa; color: #555; font-weight: 600; }
    td.empty { text-align: center; color: #999; padding: 1.5rem; }
  }

  .chip {
    display: inline-block;
    padding: 0.2rem 0.6rem;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 600;
    &.chip-active { background: #e4f5e9; color: #1a7a3c; }
    &.chip-recent { background: #e7f0fb; color: #1558b0; }
    &.chip-dormant { background: #fdf0dd; color: #a86400; }
    &.chip-opened { background: #eef0f3; color: #555; }
    &.chip-never { background: #fdecec; color: #b00020; }
  }

  .push {
    display: inline-block;
    padding: 0.2rem 0.55rem;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 600;
    &.push-on { background: #e4f5e9; color: #1a7a3c; }
    &.push-off { background: #fdecec; color: #b00020; }
    &.push-unknown { background: #f2f2f2; color: #999; }
  }

  .version {
    font-variant-numeric: tabular-nums;
    font-size: 0.8rem;
    &.version-old { color: #a86400; font-weight: 600; }
  }
  .muted { color: #bbb; }

  .tile.tile-click { cursor: pointer; &:hover { background: #fafbfc; } }

  .footnote { color: #999; font-size: 0.78rem; margin-top: 1rem; }
}
</style>
